import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { 
  Menu, 
  X, 
  Home, 
  Smartphone, 
  FileText, 
  MessageSquare, 
  User,
  LogOut,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, logout } = useAuth();
  const { isExpanded, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    if (isLoggingOut) return; // Çift tıklamayı önle
    
    setIsLoggingOut(true);
    try {
      const result = await logout();
      if (result.success) {
        navigate('/');
      } else {
        
        // Yine de ana sayfaya yönlendir
        navigate('/');
      }
    } catch (error) {
      
      // Hata olsa bile ana sayfaya yönlendir
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const primaryNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Uygulamalarım', href: '/my-apps', icon: Smartphone },
    { name: 'Başvurularım', href: '/my-applications', icon: FileText },
    { name: 'Mesajlar', href: '/messages', icon: MessageSquare },
  ];

  const secondaryNavigation = [
    { name: 'Profil', href: '/profile', icon: User },
    { name: 'Çıkış', href: '#', icon: LogOut, action: handleLogout },
  ];

  const isActive = (href) => {
    if (href === '#') return false;
    return location.pathname === href;
  };

  return (
    <>
      {/* Mobile Navbar - Aynen Korundu */}
      <nav className="md:hidden bg-white shadow-lg fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="flex-shrink-0 flex items-center space-x-2">
                <img 
                  src="/images/favicon-96x96.png" 
                  alt="12Tester Logo" 
                  className="h-8 w-8"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">12Tester</span>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-700 hover:text-emerald-600 focus:outline-none focus:text-emerald-600"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {primaryNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="flex items-center space-x-2 text-gray-700 hover:text-emerald-600 block px-3 py-2 rounded-md text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              <div className="border-t pt-2 mt-2">
                <div className="px-3 py-2 text-sm text-gray-500">
                  {user?.firstName} {user?.lastName}
                </div>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-700 hover:text-emerald-600 w-full px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="h-5 w-5" />
                  <span>Profil</span>
                </Link>
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex items-center space-x-2 text-gray-700 hover:text-red-600 w-full px-3 py-2 rounded-md text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LogOut className="h-5 w-5" />
                  <span>{isLoggingOut ? 'Çıkılıyor...' : 'Çıkış Yap'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        {/* Sidebar */}
        <div className={`fixed left-0 top-0 h-full bg-white shadow-lg z-40 transition-all duration-300 flex flex-col ${
          isExpanded ? 'w-72' : 'w-18'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-emerald-100">
            {isExpanded && (
              <div className="flex items-center space-x-2">
                <img 
                  src="/images/favicon-96x96.png" 
                  alt="12Tester Logo" 
                  className="h-8 w-8"
                />
                <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
                  12Tester
                </span>
              </div>
            )}
            {!isExpanded && (
              <img 
                src="/images/favicon-96x96.png" 
                alt="12Tester Logo" 
                className="h-8 w-8 mx-auto"
              />
            )}
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              {isExpanded ? (
                <ChevronLeft className="h-4 w-4 text-slate-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-600" />
              )}
            </button>
          </div>


          {/* Primary Navigation */}
          <div className="flex-1 px-3 py-6 space-y-3">
            {primaryNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <div key={item.name} className="relative group">
                  <Link
                    to={item.href}
                    className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 relative ${
                      active
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                  >
                    {active && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-500 to-cyan-500 rounded-r-full"></div>
                    )}
                    <Icon className={`h-5 w-5 ${isExpanded ? 'mr-3' : 'mx-auto'}`} />
                    {isExpanded && <span>{item.name}</span>}
                  </Link>
                  
                  {/* Tooltip - Sadece Compact Mode */}
                  {!isExpanded && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Secondary Navigation - Sticky Bottom */}
          <div className="px-3 py-4 border-t border-emerald-100 space-y-3 mt-auto">
            {secondaryNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <div key={item.name} className="relative group">
                  {item.action ? (
                    <button
                      onClick={item.action}
                      disabled={isLoggingOut}
                      className={`flex items-center w-full px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                        active
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Icon className={`h-5 w-5 ${isExpanded ? 'mr-3' : 'mx-auto'}`} />
                      {isExpanded && <span>{isLoggingOut ? 'Çıkılıyor...' : item.name}</span>}
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                        active
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${isExpanded ? 'mr-3' : 'mx-auto'}`} />
                      {isExpanded && <span>{item.name}</span>}
                    </Link>
                  )}
                  
                  {/* Tooltip - Sadece Compact Mode */}
                  {!isExpanded && (
                    <div className={`absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 ${
                      item.name === 'Çıkış' ? 'top-0' : 'top-1/2 -translate-y-1/2'
                    }`}>
                      {item.name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
